import type { Iso3166TwoDigitCountryCode } from '@onefootprint/request-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AddressPrediction } from '../address-input.types';
const useAddressPredictions = (country: Iso3166TwoDigitCountryCode) => {
  const [predictions, setPredictions] = useState<AddressPrediction[]>([]);
  const session = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestId = useRef(0);
  const clear = useCallback(() => {
    requestId.current += 1;
    if (timer.current) clearTimeout(timer.current);
    session.current = null;
    setPredictions([]);
  }, []);
  useEffect(() => {
    clear();
    return () => {
      requestId.current += 1;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [country, clear]);
  const search = (input: string) => {
    requestId.current += 1;
    const id = requestId.current;
    if (timer.current) clearTimeout(timer.current);
    if (!input.trim()) {
      clear();
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        if (typeof google === 'undefined' || !google.maps?.importLibrary) return;
        const {
          AutocompleteSuggestion,
          AutocompleteSessionToken
        } = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary;
        if (id !== requestId.current) return;
        session.current ??= new AutocompleteSessionToken();
        const {
          suggestions
        } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          includedRegionCodes: [country.toLowerCase()],
          language: country === 'US' ? 'en' : country === 'MX' ? 'es' : undefined,
          sessionToken: session.current
        });
        if (id === requestId.current) {
          setPredictions(suggestions.flatMap(suggestion => suggestion.placePrediction ? [suggestion.placePrediction] : []));
        }
      } catch {
        // Address entry remains available when Google cannot provide suggestions.
        if (id === requestId.current) setPredictions([]);
      }
    }, 200);
  };
  return {
    predictions,
    search,
    clear
  };
};
export default useAddressPredictions;